// type
const RES = 'RESISTOR', CAP = 'CAPACITOR', UNKNOWN = 'Unknown';
const FACTOR = ['0201', '0402', '0603', '0805', '1005', '1206', '1210', '1812', '2010', '2512', '01005'];

class PartParser {
  constructor(partName) {
    this.partName = partName;
    this.tokens = partName.split(/[_,-]/).map(t => t.trim()).filter(Boolean);
  }

  getType() {
    const t = this.tokens.map(t => t.toUpperCase());
    if (t.includes('RES') || t.includes(RES) || t.includes('R')) return RES;
    if (t.includes('CAP') || t.includes(CAP) || t.includes('C')) return CAP;
    return UNKNOWN;
  }

  getFactor() {
    for (const tok of this.tokens) {
      if (FACTOR.includes(tok)) {
        return tok;
      }

      const re = new RegExp(`(${FACTOR.join('|')})`);
      const m = tok.match(re);
      if (m) return m[1];
    }
    return null;
  }

  getResistor = () => {
    const tokens = this.tokens;
    const resTokens = tokens.map(token => FACTOR.includes(token) || FACTOR.includes(`0${token}`) ? UNKNOWN : /\d{4,}/.test(token) ? UNKNOWN : token)

    const unitValue = getUnitResistor(resTokens);
    if (unitValue) return unitValue;

    const pointValue = getPointResistor(resTokens);
    if (pointValue) return pointValue;

    const intValue = getIntResistor(resTokens, this.partName);
    if (intValue) return intValue;

    return '';
  }

  getCapacitor() {
    for (const tok of this.tokens) {
      const t = tok.toUpperCase();
      if (/^\d+(\.\d+)?(UF|NF|PF)$/.test(t)) return tok;
      if (/^\d{3}$/.test(t)) return tok;
    }
    return null;
  }

  getManufacturerCodes() {
    return this.tokens.filter(t => /^[A-Z0-9]{6,}$/i.test(t));
  }


  getNumericValue(type) {
    if (type === RES) {
      const r = this.getResistor();
      if (!r) return '';
      return this.parseResistorValue(r); // Ω
    }

    if (type === CAP) {
      const c = this.getCapacitor();
      if (!c) return '';
      return this.parseCapacitorValue(c); // F
    }

    return '';
  }

  // -------- 50K / 4R7 / 4p7 --------
  parseResistorValue(str) {
    const s = str.toUpperCase();

    // 4R7 / 4P7
    if (/^\d+[RP]\d+$/.test(s)) {
      return parseFloat(s.replace('P', '.').replace('R', '.'));
    }

    // 50K / 1M / 10R
    const m = s.match(/^(\d+(\.\d+)?)([RKM])$/);
    if (!m) return null;

    const value = parseFloat(m[1]);
    const unit = m[3];

    const mul = { R: 1, K: 1e3, M: 1e6 };
    return value * mul[unit];
  }

  // -------- 10UF / 100NF / 104 --------
  parseCapacitorValue(str) {
    const s = str.toUpperCase();

    // 10UF / 100NF / 1PF
    let m = s.match(/^(\d+(\.\d+)?)(UF|NF|PF)$/);
    if (m) {
      const value = parseFloat(m[1]);
      const unit = m[3];
      const mul = { PF: 1e-12, NF: 1e-9, UF: 1e-6 };
      return value * mul[unit];
    }

    // 104 / 103 （EIA code）
    m = s.match(/^(\d)(\d)(\d)$/);
    if (m) {
      const base = parseInt(m[1] + m[2], 10);
      const exp = parseInt(m[3], 10);
      return base * Math.pow(10, exp) * 1e-12; // pF → F
    }

    return null;
  }
}

function getUnitResistor(tokens) {
  for (let i = 0; i < tokens.length; i++) {
    const cur = tokens[i].toUpperCase();
    const next = tokens[i + 1] ? tokens[i + 1].toUpperCase() : null;

    // ------------------
    // 1. +0_5MOHM  => 0.5m
    // ------------------
    if (next) {
      const n = cur.match(/^\+?(\d+)$/);
      const m = next.match(/^(\d+)([KMR]?)(OHM)$/);
      if (n && m && n[1] && m[1] && m[2]) {
        return formatOhmValue(`${n[1]}.${m[1]}`, m[2]);
      }
    }

    // ------------------
    // 2. 0_+50MOHM / 0_+3OHM / 0_+4KOHM
    // ------------------
    if (next) {
      const m = next.match(/^\+?(\d+)([KMR]?)(OHM)$/);
      if (m && m[1] && m[2]) {
        return formatOhmValue(m[1], m[2]);
      }
    }

    // ------------------
    // 3. 4_75K / 93_1K / 10_0K
    // ------------------
    if (/^\d+$/.test(cur) && /^\d+[KM]$/.test(next)) {
      return `${tokens[i]}.${tokens[i + 1]}`;
    }

    // ------------------
    // 4. 10K / 4R7 / 4P7
    // ------------------
    if (/^\d+[RP]\d+$/.test(cur)) return tokens[i];
    if (/^\d+(\.\d+)?[RKM]$/.test(cur)) return tokens[i];
  }
  return '';
}

function getPointResistor(tokens) {
  for (let i = 0; i < tokens.length; i++) {
    const cur = tokens[i].toUpperCase();
    const next = tokens[i + 1] ? tokens[i + 1].toUpperCase() : null;

    // ------------------
    // 5. 82_0  => 82.0
    // ------------------
    if (/^\d+$/.test(cur) && /^\d+$/.test(next)) {
      return `${tokens[i]}.${tokens[i + 1]}`;
    }

    // ------------------
    // 6. 0.2 
    // ------------------
    if (/^\d+\.\d+$/.test(tokens[i])) {
      return tokens[i];
    }
  }
  return '';
}

function getIntResistor(tokens, part) {
  let minScore = -1000, bestToken = '';
  for (let i = 0; i < tokens.length; i++) {
    const cur = tokens[i].toUpperCase();
    const next = tokens[i + 1] ? tokens[i + 1].toUpperCase() : null;

    // ------------------
    // 7. 1000
    // ------------------
    if (/^\d+$/.test(cur) && (!next || !/^\+?\d+$/.test(next))) {
      const score = scoreNumberToken(tokens[i], i, tokens, part);
      if (score > minScore) {
        minScore = score;
        bestToken = tokens[i];
      }
    }
  }
  return bestToken;
}

function formatOhmValue(value, unit) {
  if (unit === 'M') return value + 'm';
  if (unit === 'K') return value + 'k';
  return value;
}

function scoreNumberToken(token, index, tokens, fullStr) {
  let score = 0;
  const t = token.toString();

  const prev = tokens[index - 1] || "";
  const next = tokens[index + 1] || "";

  // value , 1%
  if (/^\d+%$/.test(next)) score += 50;
  // value , 1/16W
  if (/^\d+\/\d+W$/i.test(next)) score += 40;

  const firstComma = fullStr.indexOf(",");
  if (firstComma !== -1) {
    const pos = fullStr.indexOf(token);
    if (pos > firstComma) score += 20;
  }

  // CHIP / MF / GENERIC / LF
  if (/^(CHIP|MF|GENERIC|LF)$/i.test(prev)) score += 20;

  // A93548
  if (/^[A-Z]+\d+$/i.test(prev)) score -= 30;


  const firstDash = fullStr.indexOf("-");
  if (firstDash !== -1) {
    const pos = fullStr.indexOf(token);
    if (pos < firstDash) score -= 20;
  }

  if (/^\d{3}$/.test(t)) score -= 10;
  return score;
}

export default PartParser;
export {
  RES,
  CAP
}